/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef, useState} from 'react';
import {loadOfflineCanRequests} from '@ion/core/src/services/Connect';
import {
  LoadOfflineCanRequestsParams,
  OfflineCanApiResponse,
  OfflineCanRequestResult,
  SortDirection
} from '@ion/core/src/types/services';
import QueryKey from '@ion/query/src/config/QueryKeys';
import {
  getOfflineCanApiErrors,
  hasNoCanRequestsFoundError,
  OfflineCanApiErrorCode
} from '@ion/query/src/hooks/useLoadOfflineCanRequests';
import usePaginatedQueryCache from '@ion/query/src/hooks/usePaginatedQueryCache';
import {UsePaginationResult} from '@ion/query/src/hooks/usePagination';
import {parseOfflineCanRequestsPage} from './CanRequestUtils';

export const CAN_REQUESTS_REFRESH_DURATION = 500;

type OfflineCanRequestsPage = OfflineCanApiResponse<OfflineCanRequestResult>;

export interface UseOfflineCanRequestsPagingOptions {
  queryParams: LoadOfflineCanRequestsParams;
  enabled?: boolean;
  initialPageSize?: number;
}

export interface UseOfflineCanRequestsPagingResult {
  loadCanRequestRows: (params: {callback: Function; page?: number; pageSize?: number; sort?: string[]}) => Promise<any>;
  refresh: boolean;
  onRefresh: () => void;
  fetching: boolean;
  paging: UsePaginationResult;
  noResultsDescription: string | null;
}

const SORTABLE_FIELDS = ['createdOn', 'updatedOn'];

const toSortParams = (sort?: string[]): Pick<LoadOfflineCanRequestsParams, 'sort' | 'order'> => {
  const [value = ''] = sort || [];
  const field        = value.replace(/^[-+]/, '');
  return SORTABLE_FIELDS.includes(field)
    ? {sort: field, order: value.startsWith(SortDirection.Descending) ? 'desc' : 'asc'}
    : {sort: 'createdOn', order: 'desc'};
};

const isNoCanRequestsFoundCode = (code?: string | number): boolean => (
  `${code || ''}` === OfflineCanApiErrorCode.NoCanRequestsFound
);

const loadOfflineCanRequestsPage = async(params: LoadOfflineCanRequestsParams): Promise<OfflineCanRequestsPage> => {
  let response: OfflineCanRequestsPage;
  try {
    response = await loadOfflineCanRequests(params);
  } catch (err) {
    if (!hasNoCanRequestsFoundError(err)) {
      throw err;
    }
    response = {
      response : {
        results    : [],
        errors     : getOfflineCanApiErrors(err?.response?.data),
        totalCount : 0
      }
    };
  }
  return response;
};

const getNoResultsDescription = (response: OfflineCanRequestsPage): string | null => (
  response?.response && parseOfflineCanRequestsPage(response).totalCount === 0
    ? getOfflineCanApiErrors(response).find((item) => isNoCanRequestsFoundCode(item?.code))?.description || ''
    : null
);

/**
 * Pages offline CAN requests for the infinite CAN requests grid. Owns the
 * grid's refresh flag, which is raised whenever the hook is enabled, the
 * query params change, or `onRefresh` is called, and tracks whether the
 * current search came back empty.
 *
 * ```typescript
 * import {useOfflineCanRequestsPaging} from './useOfflineCanRequestsPaging';
 * ...
 * const {
 *   loadCanRequestRows,
 *   refresh,
 *   onRefresh,
 *   paging
 * } = useOfflineCanRequestsPaging({
 *   queryParams : {esn: '3272001351'},
 *   enabled     : true
 * });
 * ```
 *
 * @param   queryParams     Offline CAN request filters sent with every page.
 * @param   enabled         Determines whether pages are loaded.
 * @param   initialPageSize The grid's initial page size.
 * @returns The grid loader, refresh flag and handler, paging state, and
 *          the empty-result description (`null` when not empty).
 */
export const useOfflineCanRequestsPaging = ({
  queryParams,
  enabled = true,
  initialPageSize = 20
}: UseOfflineCanRequestsPagingOptions): UseOfflineCanRequestsPagingResult => {
  const queryParamsRef                                  = useRef(queryParams);
  const [refresh, setRefresh]                           = useState(false);
  const [refreshRequest, setRefreshRequest]             = useState(0);
  const [noResultsDescription, setNoResultsDescription] = useState<string | null>(null);

  const parser = useCallback(function(response: OfflineCanRequestsPage) {
    setNoResultsDescription(getNoResultsDescription(response));
    return parseOfflineCanRequestsPage(response);
  }, []);

  const {
    loadItems,
    onRefresh: clearCachedPages,
    fetching,
    paging
  } = usePaginatedQueryCache({
    api             : loadOfflineCanRequestsPage,
    baseKey         : QueryKey.LOAD_OFFLINE_CAN_REQUESTS,
    initialPageSize,
    pageKey         : 'pg',
    pageSizeKey     : 'pgSize',
    parser,
    apiParams       : queryParams,
    enabled,
    enabledPrefetch : false,
    retry           : false
  });

  useEffect(() => {
    queryParamsRef.current = queryParams;
  }, [queryParams]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    setNoResultsDescription(null);
    if (enabled) {
      clearCachedPages(false);
      setRefresh(true);
      timeout = setTimeout(function() {
        setRefresh(false);
      }, CAN_REQUESTS_REFRESH_DURATION);
    } else {
      setRefresh(false);
    }
    return function() {
      clearTimeout(timeout);
    };
  }, [clearCachedPages, enabled, queryParams, refreshRequest]);

  const loadCanRequestRows = useCallback(function({callback, page, pageSize, sort}) {
    return loadItems({
      ...queryParamsRef.current,
      ...toSortParams(sort),
      callback,
      pg     : (page || 0) + 1,
      pgSize : pageSize
    });
  }, [loadItems]);

  const onRefresh = useCallback(() => {
    setRefreshRequest((count) => count + 1);
  }, []);

  return {
    loadCanRequestRows,
    refresh,
    onRefresh,
    fetching,
    paging,
    noResultsDescription
  };
};

export default useOfflineCanRequestsPaging;
